import { FlowThreadData } from "./FlowThreadData";
import { FlowHistory } from "./FlowHistory";
import { FlowHistoryType } from "./FlowHistoryType";
import { FlowState } from "./FlowState";
import { IConfigStorage } from "../../Common/IConfigStorage";
import { DataUtilities } from "../../Common/DataUtilities";
import { FlowStepData } from "../Steps/FlowStepData";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { FlowConstants } from "../Utilities/FlowConstants";
import { AppGlueError } from "../../AppGlueError";

export class FlowExecutionData implements IConfigStorage {
    flowId: string;
    flowExecutionId: string = DataUtilities.generateUniqueId();
    data: FlowThreadData[] = [];
    history: FlowHistory[] = [];
    current: FlowHistory[] = [];

    state: FlowState = FlowState.RUNNING;

    errorMessage: string | undefined;
    errorStackTrace: string | undefined;
    errorStepId: string | undefined;
    errorStepName: string | undefined;
    errorStepType: string | undefined;

    // this is not stored on purpose
    storeInMemory: boolean = false;

    // callback
    onComplete?: (
        resultingData: ExecutionData,
        endedInError: boolean,
        errorMessage?: string,
        errorStackTrace?: string,
        errorStep?: FlowStepData
    ) => void;

    constructor(flowId: string, data: ExecutionData) {
        this.flowId = flowId;
        // add main thread
        this.data.push(new FlowThreadData(FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID, data));
    }

    set errorStep(value: FlowStepData | undefined) {
        this.errorStepId = value?._id;
        this.errorStepName = value?.name;
        this.errorStepType = value?.__type;
    }

    static createFromData(d: object): FlowExecutionData {
        let exData = new FlowExecutionData("a", new ExecutionData());

        exData.setStorageData(d);

        return exData;
    }

    storeExecutionStart(thread: string, id: string, type: FlowHistoryType) {
        // add history to current
        this.current.push(new FlowHistory(thread, type, id));
    }

    storeExecutionComplete(thread: string, id: string, _type: FlowHistoryType, _path?: string) {
        // this method is called when a step of sequence is completed.
        let h = this.getHistoryForElement(thread, id);

        if (!h) {
            throw new Error("History element not found " + id);
        }

        h.end = new Date(Date.now());

        const index = this.current.indexOf(h, 0);
        if (index > -1) {
            this.current.splice(index, 1);
        }

        this.history.push(h);
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "flowId", this.flowId);
        Reflect.set(data, "flowExecutionId", this.flowExecutionId);
        Reflect.set(data, "state", this.state);
        Reflect.set(data, "flowId", this.flowId);
        Reflect.set(data, "errorMessage", this.errorMessage);
        Reflect.set(data, "errorStackTrace", this.errorStackTrace);
        Reflect.set(data, "errorStepId", this.errorStepId);
        Reflect.set(data, "errorStepName", this.errorStepName);
        Reflect.set(data, "errorStepType", this.errorStepType);

        Reflect.set(
            data,
            "current",
            this.current.map((c) => {
                return c.getStorageData();
            })
        );
        Reflect.set(
            data,
            "history",
            this.history.map((c) => {
                return c.getStorageData();
            })
        );

        Reflect.set(
            data,
            "data",
            this.data.map((d) => {
                return d.getStorageData();
            })
        );

        return data;
    }

    setStorageData(data: object): void {
        this.flowId = Reflect.get(data, "flowId");
        this.flowExecutionId = Reflect.get(data, "flowExecutionId");
        this.state = Reflect.get(data, "state");
        this.flowId = Reflect.get(data, "flowId");
        this.errorMessage = Reflect.get(data, "errorMessage");
        this.errorStackTrace = Reflect.get(data, "errorStackTrace");
        this.errorStepId = Reflect.get(data, "errorStepId");
        this.errorStepName = Reflect.get(data, "errorStepName");
        this.errorStepType = Reflect.get(data, "errorStepType");

        let current = Reflect.get(data, "current") as {}[];

        this.current = current.map((c) => {
            let h = new FlowHistory("a", FlowHistoryType.STEP, "a");
            h.setStorageData(c);
            return h;
        });

        let history = Reflect.get(data, "history") as {}[];

        this.history = history.map((c) => {
            let h = new FlowHistory("a", FlowHistoryType.STEP, "a");
            h.setStorageData(c);
            return h;
        });

        let datav = Reflect.get(data, "data") as {}[];

        this.data = [];

        for (let d of datav) {
            let exData = new FlowThreadData("a", new ExecutionData());
            exData.setStorageData(d);

            this.data.push(exData);
        }
    }

    getThreads(): string[] {
        return this.data.map((d) => {
            return d.threadId;
        });
    }

    getThreadData(id: string): FlowThreadData {
        if (this.data.length === 1 && this.data[0].threadId === id) return this.data[0];

        let ret = this.data.find((d) => {
            return d.threadId === id;
        });

        if (!ret) throw new AppGlueError("thread data not found " + id);

        return ret;
    }

    getExecutionDataForThread(thread: string = FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID): ExecutionData {
        return this.getThreadData(thread).executionData;
    }

    getSourceForThread(thread: string = FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID): string | undefined {
        return this.getThreadData(thread).fromThread;
    }

    startThread(fromThread: string): FlowThreadData {
        let d = this.getExecutionDataForThread(fromThread);
        let newData = new ExecutionData();

        newData.setStorageData(d.getStorageData());
        newData.datalocked = false;

        let newId = DataUtilities.generateUniqueId();

        let threadData = new FlowThreadData(newId, newData);
        threadData.fromThread = fromThread;

        this.data.push(threadData);

        return threadData;
    }

    mergeThreadToParent(thread: string) {
        let d = this.getThreadData(thread);

        let parent = this.getExecutionDataForThread(d.fromThread);

        for (let v of d.executionData.getValueNames()) {
            parent.set(v, d.executionData.get(v));
        }

        this.removeThread(thread);
    }

    removeThread(id: string) {
        this.data = this.data.filter((td) => {
            return td.threadId !== id;
        });
    }

    getHistoryForElement(thread: string, stepId: string): FlowHistory {
        // check current
        for (let h of this.current) {
            if (h.threadId === thread && h.elementId == stepId) {
                return h;
            }
        }

        // check history
        for (let h of this.history) {
            if (h.threadId === thread && h.elementId == stepId) {
                return h;
            }
        }

        throw new Error("History not found for thread/step " + thread + "/" + stepId);
    }
}
